import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, OnInit, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AdminuiAutocompleteComponent, AutoCompleteSelectEvent } from '../components/forms/adminui-autocomplete/adminui-autocomplete.component';
import { AdminuiAutocompleteGroupDirective } from '../components/forms/adminui-autocomplete/adminui-autocomplete-group.directive';
import { first, firstValueFrom, map, Subject, takeUntil } from 'rxjs';
import '@visma-swno/gaia-chat-ui';
import type { VsnAuth, NavigationMenu, LanguageCode, TreeLeaf, AppModules, FeedbackConfig } from '@visma-swno/vsn-navigation';
import { AuthService } from '../core/auth/auth.service';
import { ProfileService } from '../core/auth/profile.service';
import { PROFILE_ROLE } from '../core/auth/profile-role';
import { Context } from '../core/auth/context';
import { ADMIN_UI_TOKEN_KIND, TOKEN_KIND_KALMAR } from '../core/auth/storage-keys';
import { DomainService } from '../core/domain/domain.service';
import { WindowRef } from '../core/domain/window-ref';
import { I18NService } from '../core/i18n/i18n.service';
import { NavbarService } from './navbar.service';
import { ContextGroup } from './context-group';
import { AUTOCOMPLETE_MAX_LIMIT } from './autocomplete-limit';
import { TOKEN_RESPONSE } from '../core/auth/connect-storage-keys';
import { LatestExportComponent } from '../exports/latest-export.component';
import { LatestExportsService } from '../exports/latest-exports.service';

@Component({
    selector: 'adminui-nav-shell',
    imports: [FormsModule, AdminuiAutocompleteComponent, AdminuiAutocompleteGroupDirective, LatestExportComponent],
    templateUrl: './nav-shell.component.html',
    styleUrl: './nav-shell.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NavShellComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('gaiaChat') gaiaChat?: ElementRef;

    vsnAuth?: VsnAuth;
    vsnTree?: NavigationMenu;
    vsnLang: LanguageCode = 'en';
    vsnHelpUrl?: string;
    vsnLandingPageUrl?: string;
    vsnModules?: AppModules;
    vsnFeedback?: FeedbackConfig;
    
    message: { [key: string]: string } = {};
    contextGroups: ContextGroup[] = [];
    selectedContext!: Context;

    profileId = '';
    baseUrl = '';
    lang = '';
    isGaiaChatOpen = false;
    isNotificationPanelOpen = false;
    isNotificationActive = false;
    private destroy$ = new Subject<void>();

    private hostname = ''
    private closeRequestedHandler = () => { this.isGaiaChatOpen = false; };
    private authFailedHandler = (event: Event) => { console.error('Gaia auth failed', event); };
    private requestTokenHandler!: (event: Event) => void;
    private logoutClickHandler!: () => void;
    private menuItemChangedHandler!: (e: Event) => void;
    private navigateHandler!: (e: Event) => void;
    private aiAssistantClickHandler!: () => void;
    private notificationPanelHandler!: () => void;

    private authService = inject(AuthService);
    private profileService = inject(ProfileService);
    private navbarService = inject(NavbarService);
    private domainService = inject(DomainService);
    private windowRef = inject(WindowRef);
    private i18nService = inject(I18NService);
    private router = inject(Router);
    private elementRef = inject(ElementRef);
    private latestExportsService = inject(LatestExportsService);

    ngOnInit(): void {
        this.hostname = this.windowRef.getHostname();
        this.requestTokenHandler = this.handleTokenRequest.bind(this);
        this.initVsnNavigation();
        this.initGaia();
        this.loadContextGroups();
        this.setupEventListeners();
        this.elementRef.nativeElement.addEventListener('close-requested', this.closeRequestedHandler);
        this.elementRef.nativeElement.addEventListener('request-token', this.requestTokenHandler);
        this.handleNotificationPanel();
    }

    ngAfterViewInit(): void {
        if (this.gaiaChat) {
            this.gaiaChat.nativeElement.authStrategy = 'visma';
            this.gaiaChat.nativeElement.tokenProvider = () => this.authService.getAccessToken();
            this.gaiaChat.nativeElement.addEventListener('auth-failed', this.authFailedHandler);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.elementRef.nativeElement.removeEventListener('close-requested', this.closeRequestedHandler);
        this.elementRef.nativeElement.removeEventListener('request-token', this.requestTokenHandler);
        const hostEl = this.elementRef.nativeElement as HTMLElement;
        hostEl.removeEventListener('vsn-logout-click', this.logoutClickHandler);
        hostEl.removeEventListener('vsn-menu-item-changed', this.menuItemChangedHandler);
        hostEl.removeEventListener('vsn-navigate', this.navigateHandler);
        hostEl.removeEventListener('vsn-ai-assistant-click', this.aiAssistantClickHandler);
        if (this.gaiaChat) {
            this.gaiaChat.nativeElement.removeEventListener('auth-failed', this.authFailedHandler);
        }
        hostEl.removeEventListener('vsn-notification-click', this.notificationPanelHandler);
    }

    onSearchContext(event: any): void {
        const page = 1;
        const limit = AUTOCOMPLETE_MAX_LIMIT;
        if (event.originalEvent.type === 'click') {
            this.authService.getContextResponse(page, limit).pipe(first()).subscribe(data => {
                this.contextGroups = this.navbarService.getContextGroups(data.contexts, this.message);
            });
        } else {
            this.authService.getContextResponse(page, limit, event.query).pipe(first()).subscribe(data => {
                this.contextGroups = data ? this.navbarService.getContextGroups(data.contexts, this.message) : [];
            });
        }
    }

    onSelectContext(event: AutoCompleteSelectEvent): void {
        const context: Context = event.value;
        if (this.profileService.profile.currentContext.contextId !== context.contextId) {
            this.profileService.profile.currentContext = context;
            this.authService.setCurrentContextOnStorage(context);
            if (context.roles?.includes(PROFILE_ROLE.administrator)) {
                // Kalmar (non-tenant) context: switch to a Kalmar token. No AMA patch (endpoint is AMA-only);
                // the context is persisted in session storage above.
                sessionStorage.setItem(ADMIN_UI_TOKEN_KIND, TOKEN_KIND_KALMAR);
                this.windowRef.setLocationHref(this.authService.getConnectLoginUrl(this.hostname, { kalmarToken: true }));
            } else {
                // Tenant (AMA) context: the tenant callback persists lastUsedTenantId once the tenant token is issued.
                sessionStorage.removeItem(ADMIN_UI_TOKEN_KIND);
                this.windowRef.setLocationHref(this.authService.getConnectLoginUrl(this.hostname));
            }
        }
    }

    onFocus(event: Event): void {
        (event.currentTarget as HTMLInputElement).select();
    }

    onBlurContext(): void {
        this.selectedContext = structuredClone(this.profileService.profile.currentContext);
    }

    private initVsnNavigation(): void {
        const profile = this.profileService.profile;
        const navbarMsg: { [key: string]: string } = this.i18nService.messages['navbar'] || {};
        const isProduction = this.domainService.isProduction(this.hostname);

        this.message = {
            ...(this.i18nService.messages['navbar'] || {}),
            ...(this.i18nService.messages['help'] || {}),
            ...(this.i18nService.messages['common'] || {})
        };

        const langCode = profile.locale.split('-')[0];
        const supportedLangs: LanguageCode[] = ['en', 'no', 'nb', 'nn', 'sv', 'nl', 'fi', 'da'];
        this.vsnLang = supportedLangs.includes(langCode as LanguageCode) ? langCode as LanguageCode : 'en';

        this.vsnAuth = {
            user: {
                userId: profile.sub,
                userName: profile.name,
                userEmail: profile.email,
            },
            profilePictureBaseUrl: this.domainService.getVsnProfilePictureBaseUrl(this.hostname),
            myProfileUrl: this.domainService.getVsnMyProfileUrl(this.hostname),
        };

        this.vsnLandingPageUrl = this.domainService.getVsnLandingPageUrl(this.hostname);

        switch (profile.locale) {
            case 'nb-NO': this.vsnHelpUrl = 'https://docs.vismasoftware.no/no/admin/'; break;
            case 'sv-SE': this.vsnHelpUrl = 'https://docs.vismasoftware.no/sv/admin/'; break;
            default: this.vsnHelpUrl = 'https://docs.vismasoftware.no/admin/';
        }

        const roles = profile.roles;
        const rootPath = this.navbarService.getRootContextPath(roles, profile.currentContext.taskId);
        const userAccessLabel = navbarMsg['menuItemUserAccess'] || 'User access';
        const customersLabel = navbarMsg['menuItemCustomers'] || 'Customers';
        const onPremisesIntegrationsLabel = navbarMsg['menuItemOnPremisesIntegration'] || 'On-premises integration';

        const treeLeaves: TreeLeaf[] = [];
        let intermediateId!: string;
        let intermediateLabel!: string;

        const isBuRole = roles.includes(PROFILE_ROLE.buAccessAdministrator) || roles.includes(PROFILE_ROLE.buSubscriberViewer);

        if (isBuRole) {
            intermediateId = 'business-unit';
            intermediateLabel = this.message['labelBusinessUnit'];
            if (roles.includes(PROFILE_ROLE.buAccessAdministrator)) {
                treeLeaves.push({ id: 'user-access', label: userAccessLabel, url: rootPath + '/user-access' });
            }
            if (roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
                treeLeaves.push({ id: 'subscribers', label: customersLabel, url: rootPath + '/subscribers/user-access' });
            }
        } else if (roles.includes(PROFILE_ROLE.subscriberAdministrator)) {
            intermediateId = 'subscriber';
            intermediateLabel = this.message['labelSubscriber'];
            treeLeaves.push({ id: 'user-access', label: userAccessLabel, url: rootPath + '/user-access' });
            if (this.profileService.onPremiseIntegration  && !isProduction) {
                treeLeaves.push({ id: 'onPremisesIntegrations', label: onPremisesIntegrationsLabel, url: rootPath + '/on-premises-integrations' });
            }
        } else if (roles.includes(PROFILE_ROLE.companyAdministrator)) {
            intermediateId = 'company';
            intermediateLabel = this.message['labelCompany'];
            treeLeaves.push({ id: 'user-access', label: userAccessLabel, url: rootPath + '/user-access' });
        } else if (roles.includes(PROFILE_ROLE.administrator)) {
            if (profile.currentContext.taskId) {
                intermediateId = 'customer_onboarding';
                intermediateLabel = this.message['labelCustomerOnboarding'];
            } else {
                intermediateId = 'provisioning';
                intermediateLabel = this.message['labelProvisioning'];
            }
        }

        const currentAppId = 'Access_Management';
        const currentAppName = 'Admin';

        this.vsnTree = {
            items: [{
                id: currentAppId,
                label: currentAppName,
                items: [{
                    id: intermediateId,
                    label: intermediateLabel,
                    ...(treeLeaves.length > 0
                        ? { items: treeLeaves as [TreeLeaf, ...TreeLeaf[]] }
                        : { url: rootPath })
                }]
            }],
            currentItem: roles.includes(PROFILE_ROLE.buSubscriberViewer) && !roles.includes(PROFILE_ROLE.buAccessAdministrator)
                ? 'subscribers'
                : roles.includes(PROFILE_ROLE.administrator)
                    ? intermediateId
                    : 'user-access',
            search: {
                enabled: true
            }
        };
        
        this.vsnModules = {
            activeModuleId: currentAppId,
            activeModuleLabel: currentAppName,
            modules: () => firstValueFrom(
                this.navbarService.getApplicationsVersion2().pipe(
                    map(apps =>
                        apps.map(a => ({
                            id: a.id || a.name.toLowerCase(),
                            label: a.name,
                            url: a.url
                        }))
                    )
                )
            )
        };

        this.vsnFeedback = {
            workspaceId: 'f095728acd541ae64ae99c04ca666a32',
            surveyId: 'ea10f1b88a24b305'
        }

        this.selectedContext = structuredClone(profile.currentContext);
    }

    private initGaia(): void {
        const profile = this.profileService.profile;
        this.profileId = 'admin-prod';
        this.baseUrl = this.domainService.getGaiaChatApiUrl(this.hostname);
        this.lang = profile.locale.split('-')[0];
    }

    private loadContextGroups(): void {
        this.authService.getContextResponse(1, AUTOCOMPLETE_MAX_LIMIT).pipe(first()).subscribe(data => {
            this.contextGroups = this.navbarService.getContextGroups(data.contexts, this.message);
        });
    }

    private setupEventListeners(): void {
        const hostEl = this.elementRef.nativeElement as HTMLElement;

        this.logoutClickHandler = () => { this.router.navigate(['/logout']); };
        this.menuItemChangedHandler = (e: Event) => {
            const leaf = (e as CustomEvent<TreeLeaf>).detail;
            this.router.navigateByUrl(leaf.url);
            if (this.vsnTree) {
                this.vsnTree = { ...this.vsnTree, currentItem: leaf.id };
            }
        };
        this.navigateHandler = (e: Event) => {
            const item = (e as CustomEvent<{ url: string }>).detail;
            this.windowRef.setLocationHref(item.url);
        };
        this.aiAssistantClickHandler = () => {
            this.isNotificationPanelOpen = false;
            this.isGaiaChatOpen = !this.isGaiaChatOpen;
        };
        this.notificationPanelHandler = () => {
            this.isGaiaChatOpen = false;
            this.isNotificationPanelOpen = !this.isNotificationPanelOpen;
            if (this.isNotificationPanelOpen) {
                this.latestExportsService.loadLatestExports();
            }
        };

        hostEl.addEventListener('vsn-logout-click', this.logoutClickHandler);
        hostEl.addEventListener('vsn-menu-item-changed', this.menuItemChangedHandler);
        hostEl.addEventListener('vsn-navigate', this.navigateHandler);
        hostEl.addEventListener('vsn-ai-assistant-click', this.aiAssistantClickHandler);
        hostEl.addEventListener('vsn-notification-click', this.notificationPanelHandler);
    }

    private handleTokenRequest(event: Event): void {
        const customEvent = event as CustomEvent;
        const { callback } = customEvent.detail;
        const tokenStr = sessionStorage.getItem(TOKEN_RESPONSE);
        if (tokenStr) {
            try {
                const token = JSON.parse(tokenStr);
                callback(token.access_token);
            } catch (e) {
                console.error('Failed to parse token from session storage', e);
                callback(undefined);
            }
        } else {
            callback(undefined);
        }
    }

    private handleNotificationPanel(): void {
        this.latestExportsService.openLatestExportsPanel$
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                if (value) {
                    this.isGaiaChatOpen = false;
                    this.isNotificationPanelOpen = true;
                    this.latestExportsService.loadLatestExports();
                } else {
                    this.isNotificationPanelOpen = false;
                }
            });

        this.latestExportsService.latestExports$
            .pipe(takeUntil(this.destroy$))
            .subscribe(latestExports => {
                this.isNotificationActive = latestExports.length > 0;
            });

        this.latestExportsService.loadLatestExports();
    }
}
